// src/PaymentMembership.tsx
//
// Platba členského příspěvku (/paymentmembership?year=RRRR). Titulek a tlačítko
// Zpět v hlavičce dodává Screen v App.tsx; stránka skládá: stav členství pro
// zvolený rok, kartu s údaji střelce a příspěvku, platební pokyny (sdílená
// PaymentInstructions; QR kód a e-mail jen s IBAN klubu) a tlačítko zpět
// na přehled.
//
// Formát VS (YY + 099 + SEQ), zpráva pro příjemce, SPAYD i parametry e-mailu
// žijí v utils/payments.ts a NESMÍ se měnit (docs/redesign-plan.md, krok 6).
// Stránka nic nezapisuje – stav členství (users.membership[rok]) nastavuje
// správce po spárování platby v bance.

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DocumentData, collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import {
  AsyncState,
  EmptyState,
  ErrorState,
  LoadingState,
  useAsync,
} from "./ui/AsyncState";
import { Section } from "./ui/Section";
import { DetailList, DetailRow, InfoCard } from "./ui/Cards";
import { ChoiceGroup, type ChoiceOption } from "./ui/Toggle";
import { PaymentInstructions } from "./ui/PaymentInstructions";
import { useCurrentUser } from "./utils/useCurrentUser";
import { isValidSequenceNumber } from "./utils/memberNumber";
import { CONTACT_EMAIL } from "./utils/contact";
import {
  buildMembershipVariableSymbol,
  buildPaymentQrData,
  formatCzk,
  loadBankDetails,
  membershipPaymentMessage,
  sendMembershipPaymentEmail,
} from "./utils/payments";

/** Aktivní členský příspěvek z kolekce membershipFees. */
interface MembershipFee {
  year: number;
  /** Výše příspěvku v Kč; NaN, když v dokumentu chybí nebo není číslo. */
  price: number;
}

/** Hodnota users.membership[rok], která znamená zaplaceno (shodně s dashboardem). */
const PAID_STATUS = "ZAPLACENO";

/** Kontakt pro stavy, které střelec sám nevyřeší (chybějící údaje v databázi). */
const CONTACT_ADMIN = `Napište nám prosím na ${CONTACT_EMAIL}.`;

/**
 * Aktivní příspěvky seřazené podle roku – stejný zdroj a pole jako sekce
 * Členství na dashboardu (active, year, price). Duplicitní rok vyhraje první
 * dokument (dřív feeArray[0]).
 */
async function loadMembershipFees(): Promise<MembershipFee[]> {
  const snapshot = await getDocs(collection(db, "membershipFees"));
  const fees: MembershipFee[] = [];
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (!data.active) return;
    const year = Number(data.year);
    if (!Number.isFinite(year)) return;
    if (fees.some((f) => f.year === year)) return;
    fees.push({ year, price: Number(data.price) });
  });
  fees.sort((a, b) => a.year - b.year);
  return fees;
}

/** ?year=2026 → 2026; chybějící nebo nečíselná hodnota → null. */
const parseYearParam = (raw: string | null): number | null => {
  if (!raw) return null;
  const year = parseInt(raw, 10);
  return Number.isFinite(year) ? year : null;
};

/** membership[rok] je "ZAPLACENO" (bez ohledu na velikost písmen – jako dashboard). */
const isMembershipPaid = (userDoc: DocumentData, year: number): boolean => {
  const membership = userDoc.membership;
  const status =
    membership && typeof membership === "object"
      ? membership[String(year)]
      : undefined;
  return typeof status === "string" && status.toUpperCase() === PAID_STATUS;
};

/** Textové pole dokumentu s náhradou za prázdnou/chybějící hodnotu. */
const textField = (value: unknown, fallback: string): string =>
  typeof value === "string" && value ? value : fallback;

interface ContentProps {
  /** Nové načtení přihlášeného uživatele (rodič obsah přemountuje). */
  onRetry: () => void;
}

const MembershipPaymentContent: React.FC<ContentProps> = ({ onRetry }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryYear = parseYearParam(
    new URLSearchParams(location.search).get("year")
  );

  const { user, userDoc, loading: userLoading, missing } = useCurrentUser();
  const fees = useAsync(loadMembershipFees, []);
  // null = nenastaveno (ani číslo účtu, ani IBAN); samotný IBAN může být
  // prázdný. Chyba sítě skončí jako status "error" s retry.
  const bank = useAsync(() => loadBankDetails(), []);

  // Rok zvolený přepínačem; null = podle ?year=, jinak první nezaplacený.
  const [chosenYear, setChosenYear] = useState<number | null>(null);
  useEffect(() => {
    setChosenYear(null);
  }, [queryYear]);

  const goToDashboard = () => navigate("/user");

  if (userLoading || fees.status === "idle" || fees.status === "loading") {
    return <LoadingState text="Načítám údaje o členství…" />;
  }
  if (fees.status === "error") {
    return <ErrorState error={fees.error} onRetry={fees.reload} />;
  }
  if (!userDoc && missing) {
    // Dokument users/{uid} neexistuje – „Zkusit znovu" nepomůže. Registraci
    // nebo nedokončené smazání účtu dokončí přihlašovací stránka (AuthPage);
    // stejný text jako Profil, Úprava profilu a Přihláška (nález 6 3. kola).
    // Zpět na přehled vede šipka v hlavičce.
    return (
      <EmptyState
        icon="bi-person-x"
        title="Profil nenalezen"
        text={`K účtu chybí údaje člena – registrace se nedokončila. Dokončete ji tlačítkem níže (je k tomu potřeba připojení k internetu). Pokud jste účet mazali, dokončíte tam i smazání. Když to nepůjde, napište nám na ${CONTACT_EMAIL}.`}
        action={{ label: "Dokončit registraci", onClick: () => navigate("/login") }}
      />
    );
  }
  if (!userDoc) {
    // Čtení dokumentu selhalo (detail zapsal useCurrentUser).
    return (
      <EmptyState
        icon="bi-person-exclamation"
        title="Údaje se nepodařilo načíst"
        text="Zkontrolujte připojení k internetu a zkuste to znovu."
        action={{ label: "Zkusit znovu", onClick: onRetry }}
      />
    );
  }

  const feeList = fees.data || [];
  if (feeList.length === 0) {
    return (
      <EmptyState
        icon="bi-calendar-x"
        title={
          queryYear
            ? `Pro rok ${queryYear} není vypsaný členský příspěvek`
            : "Není vypsaný žádný členský příspěvek"
        }
        text="Jakmile správce příspěvek vypíše, objeví se na přehledu."
        action={{ label: "Zpět na přehled", onClick: goToDashboard }}
      />
    );
  }

  // Výchozí rok: první nezaplacený, jinak první vypsaný.
  const defaultFee =
    feeList.find((f) => !isMembershipPaid(userDoc, f.year)) || feeList[0];
  const effectiveYear = chosenYear ?? queryYear ?? defaultFee.year;
  const fee = feeList.find((f) => f.year === effectiveYear);
  if (!fee) {
    // ?year= míří na rok bez aktivního příspěvku (starý odkaz) – nabídneme vypsaný rok.
    return (
      <EmptyState
        icon="bi-calendar-x"
        title={`Pro rok ${effectiveYear} není vypsaný členský příspěvek`}
        text={`Aktuálně je vypsaný příspěvek na rok ${defaultFee.year}.`}
        action={{
          label: `Zobrazit rok ${defaultFee.year}`,
          onClick: () => setChosenYear(defaultFee.year),
        }}
      />
    );
  }

  const paid = isMembershipPaid(userDoc, fee.year);
  // Náhrady za chybějící jméno jsou stejné jako dřív (dostávaly se i do e-mailu).
  const firstName = textField(userDoc.firstName, "NEZNAMÉ");
  const lastName = textField(userDoc.lastName, "NEZNAMÝ");
  const email = textField(userDoc.email, user?.email || "");
  // Členské číslo je v users uložené už doplněné nulami (string); do VS jde
  // beze změny. Stejná kontrola jako při přidělování čísel: odmítne prázdnou
  // hodnotu i řetězec "undefined" z importovaných záznamů.
  const rawSequence: unknown = userDoc.sequenceNumber;
  const sequenceNumber: string | number | null = isValidSequenceNumber(
    rawSequence
  )
    ? (rawSequence as string | number)
    : null;
  const priceOk = Number.isFinite(fee.price) && fee.price > 0;

  const yearOptions: ChoiceOption<string>[] = feeList.map((f) => ({
    value: String(f.year),
    label: String(f.year),
    icon: isMembershipPaid(userDoc, f.year) ? "bi-check-lg" : undefined,
  }));

  /** Platební pokyny, nebo důvod, proč je nejde sestavit. */
  const renderPayment = (): React.ReactNode => {
    if (sequenceNumber === null) {
      return (
        <InfoCard
          className="mb-3"
          icon="bi-exclamation-triangle"
          tone="warning"
          title="Chybí vaše členské číslo"
          subtitle={`Bez něj nejde sestavit variabilní symbol. ${CONTACT_ADMIN}`}
        />
      );
    }
    if (!priceOk) {
      return (
        <InfoCard
          className="mb-3"
          icon="bi-exclamation-triangle"
          tone="warning"
          title={`Výše příspěvku pro rok ${fee.year} není nastavená`}
          subtitle={CONTACT_ADMIN}
        />
      );
    }

    const bankDetails = bank.status === "ready" ? bank.data : null;
    let instructions: React.ReactNode;
    if (bankDetails) {
      const variableSymbol = buildMembershipVariableSymbol(
        fee.year,
        sequenceNumber
      );
      const message = membershipPaymentMessage(lastName, fee.year);
      // SPAYD řetězec jde sestavit jen s IBAN; e-mail nese QR kód, takže bez
      // IBAN není ani on. PaymentInstructions pak ukáže jen číslo účtu + VS
      // a místo QR poznámku.
      const qrData =
        bankDetails.iban !== ""
          ? buildPaymentQrData({
              iban: bankDetails.iban,
              amount: fee.price,
              message,
              variableSymbol,
            })
          : undefined;
      const onSendEmail =
        qrData !== undefined && email
          ? () =>
              sendMembershipPaymentEmail({
                year: fee.year,
                firstName,
                lastName,
                fee: fee.price,
                variableSymbol,
                email,
                qrData,
              })
          : undefined;
      instructions = (
        <PaymentInstructions
          amount={fee.price}
          variableSymbol={variableSymbol}
          message={message}
          bankDetails={bankDetails}
          qrData={qrData}
          recipientNote={`${variableSymbol} ${lastName}`}
          emailSentLabel={`Odesláno na ${email}`}
          onSendEmail={onSendEmail}
        >
          <p className="small text-muted mt-3 mb-0">
            Po připsání platby na účet se automaticky spáruje (přes den do 15
            minut, v noci do hodiny) a stav členství se změní na zaplaceno.
          </p>
        </PaymentInstructions>
      );
    } else {
      // loadBankDetails vrátilo null (dokument chybí, nebo nemá ani číslo účtu,
      // ani IBAN); během načítání a při chybě vykreslí stav AsyncState, tenhle
      // prvek se nepoužije.
      instructions = (
        <InfoCard
          className="mb-3"
          icon="bi-bank"
          tone="warning"
          title="Bankovní údaje nejsou nastavené"
          subtitle={`Platbu zatím nejde připravit. ${CONTACT_ADMIN}`}
        />
      );
    }

    return (
      <AsyncState
        status={bank.status}
        loadingText="Načítám platební údaje…"
        error={bank.error}
        onRetry={bank.reload}
      >
        {instructions}
      </AsyncState>
    );
  };

  return (
    <>
      {paid ? (
        <InfoCard
          className="mb-3"
          icon="bi-check-lg"
          tone="success"
          title={`Členství na rok ${fee.year} je zaplacené`}
          subtitle="Platba je spárovaná, nic dalšího není potřeba."
        />
      ) : (
        <InfoCard
          className="mb-3"
          icon="bi-credit-card"
          tone="warning"
          title={`Členství na rok ${fee.year} čeká na platbu`}
          subtitle={priceOk ? `K úhradě ${formatCzk(fee.price)}` : undefined}
        />
      )}

      <Section title="Členský příspěvek" icon="bi-person-badge">
        {feeList.length > 1 ? (
          <ChoiceGroup
            id="membership-year"
            label="Rok členství"
            options={yearOptions}
            value={String(fee.year)}
            onChange={(value) => setChosenYear(parseInt(value, 10))}
            className="mb-3"
          />
        ) : null}
        <DetailList>
          <DetailRow label="Člen" value={`${firstName} ${lastName}`} />
          <DetailRow
            label="Členské číslo"
            value={sequenceNumber === null ? "—" : String(sequenceNumber)}
          />
          <DetailRow label="Rok" value={String(fee.year)} />
          <DetailRow
            label="Výše příspěvku"
            value={priceOk ? formatCzk(fee.price) : "—"}
          />
        </DetailList>
      </Section>

      {paid ? null : renderPayment()}

      <div className="d-grid">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={goToDashboard}
        >
          <i className="bi bi-arrow-left me-2" aria-hidden="true" />
          Zpět na přehled
        </button>
      </div>
    </>
  );
};

/**
 * Obal s klíčem: „Zkusit znovu" při nenačteném profilu obsah přemountuje,
 * useCurrentUser se přihlásí znovu a dokument načte znovu (hook nemá reload).
 */
const PaymentMembership: React.FC = () => {
  const [attempt, setAttempt] = useState(0);
  return (
    <MembershipPaymentContent
      key={attempt}
      onRetry={() => setAttempt((n) => n + 1)}
    />
  );
};

export default PaymentMembership;
